import { useQuery } from '@tanstack/react-query';
import { GOOGLE_MAPS_API_KEY } from 'components/globals';

import type { MarketShape } from '../map/types';
import { getBoundsFromShape } from '../map/utils';
import type { SavedSearchFieldsFragment } from './__generated__/saved-searches.zeus';
import { resolveSavedSearchArea } from './savedSearchArea';

/** The saved search card's map: how much of it shows and how it fades in. */
export type SavedSearchMapTuning = {
  /** The map image, in CSS px, fitted to the saved area; drawn at 2x. */
  image: { width: number; height: number };
  /** Where the image sits in its 101 x 100 window, in CSS px. */
  offset: { x: number; y: number };
  /** Google's saturation adjustment for the image, -100 to 100; the card shows it on hover. */
  saturation: number;
  /** Saturation at rest, -100 to 100, applied over the image until the card is hovered. */
  restSaturation: number;
  /** The radial fade: its centre as a percentage of the window, and its radii in px. */
  mask: { x: number; y: number; rx: number; ry: number; solidUntil: number };
};

/** The card shows the area the visitor had on screen, fitted to the window. */
export const SAVED_SEARCH_MAP: SavedSearchMapTuning = {
  image: { width: 101, height: 101 },
  offset: { x: 0, y: 0 },
  saturation: -20,
  restSaturation: -100,
  mask: { x: 100, y: 0, rx: 105, ry: 120, solidUntil: 0 },
};

/** The same key the map itself loads with; local runs set only the public one. */
const mapsApiKey = () => process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY;

/** A static map fitted to the area a saved search covers, or null for a shape without bounds. */
export const savedSearchStaticMapUrl = (
  shape: MarketShape,
  { image, saturation }: SavedSearchMapTuning = SAVED_SEARCH_MAP,
): string | null => {
  let bounds: ReturnType<typeof getBoundsFromShape>;
  try {
    bounds = getBoundsFromShape(shape);
  } catch {
    return null;
  }
  const [[west, south], [east, north]] = bounds;
  const params = new URLSearchParams({
    size: `${Math.round(image.width)}x${Math.round(image.height)}`,
    scale: '2',
    maptype: 'roadmap',
    visible: `${south},${west}|${north},${east}`,
    key: mapsApiKey(),
  });
  params.append('style', 'feature:poi|visibility:off');
  params.append('style', `feature:all|saturation:${Math.round(saturation)}`);
  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
};

/** The card's map image for a saved search; null until its area is known, or when it has none. */
export const useSavedSearchMapUrl = (savedSearch: SavedSearchFieldsFragment): string | null => {
  const { market, zoneIds, vertices } = savedSearch.location;
  const { data } = useQuery({
    // A refreshed list can move a saved search's area under the same id.
    queryKey: ['saved-search-area', savedSearch.id, market, zoneIds, vertices],
    queryFn: ({ signal }) => resolveSavedSearchArea(savedSearch, signal),
    staleTime: Infinity,
    retry: false,
  });
  return data ? savedSearchStaticMapUrl(data.marketInfo.shape) : null;
};
